/**
 * conditionEvaluator
 * -------------------
 * Safe evaluator for the menu-visibility boolean expressions that are stored in
 * the database as JS-style strings (see `filtering_conditions` on menu items).
 *
 * The strings come from the DB, so we must NOT hand them to `eval()` or
 * `new Function()` — that would be a code-injection vector. Instead this module
 * implements a tiny tokenizer + recursive-descent parser that only understands
 * the operators defined by the grammar below. Anything outside the grammar is
 * treated as a plain comparison clause or causes a controlled parse error.
 *
 * Grammar (lowest precedence first):
 *   or      := and ( "||" and )*
 *   and     := unary ( "&&" unary )*
 *   unary   := "!" unary            // negation of a group/clause
 *            | primary              // note: "![...]" is a leaf, NOT a negation
 *   primary := "(" or ")"
 *            | comparison
 *
 * Leaf comparisons (all values compared as trimmed strings):
 *   key=val | key=v1,v2,...            -> ctx[key] equals ANY listed value
 *   key!=val | key!=v1,v2,...          -> ctx[key] equals NONE of the values
 *   [v1,v2,...].includes(key)          -> ctx[key] is in the list
 *   ![v1,v2,...].includes(key)         -> ctx[key] is NOT in the list
 *
 * Fail-open behavior (kept identical to the Angular implementation so both
 * systems stay consistent):
 *   - empty / null / whitespace condition        -> true  (visible)
 *   - comparison referencing a key absent in ctx -> true  (don't hide unknowns)
 *   - any parse error                            -> warn + true
 */

export type ConditionContext = Record<string, string>;

/** Split a comma list into trimmed, non-empty string values. */
const splitValues = (value: string): string[] =>
  value
    .split(',')
    .map((v) => v.trim())
    .filter((v) => v !== '');

/**
 * Evaluate a single leaf comparison clause (no boolean operators inside).
 * Returns true for empty clauses and for clauses that reference unknown keys.
 */
const evalComparison = (clause: string, ctx: ConditionContext): boolean => {
  const trimmed = clause.trim();
  if (!trimmed) return true;

  // [v1,v2].includes(key)  and  ![v1,v2].includes(key)
  const includesMatch = trimmed.match(
    /^(!?)\[([^\]]*)\]\.includes\(\s*(\w+)\s*\)$/
  );
  if (includesMatch) {
    const negated = includesMatch[1] === '!';
    const values = splitValues(includesMatch[2]);
    const actual = ctx[includesMatch[3].trim()];
    if (actual === undefined) return true; // unknown key -> fail open
    const hit = values.includes(actual);
    return negated ? !hit : hit;
  }

  // key!=v1,v2  (check "!=" before "=" so it isn't misread as an "=" clause)
  if (trimmed.includes('!=')) {
    const [k, v] = trimmed.split('!=').map((s) => s.trim());
    const actual = ctx[k];
    if (actual === undefined) return true; // unknown key -> fail open
    return !splitValues(v).includes(actual);
  }

  // key=v1,v2
  if (trimmed.includes('=')) {
    const [k, v] = trimmed.split('=').map((s) => s.trim());
    const actual = ctx[k];
    if (actual === undefined) return true; // unknown key -> fail open
    return splitValues(v).includes(actual);
  }

  // Unrecognized clause shape -> fail open rather than hide the item.
  return true;
};

/**
 * Internal recursive-descent parser/evaluator. Throws on malformed input; the
 * public `evalCondition` wrapper is responsible for the fail-open catch.
 */
const parseExpression = (src: string, ctx: ConditionContext): boolean => {
  let pos = 0;

  const skipWs = (): void => {
    while (pos < src.length && /\s/.test(src[pos])) pos++;
  };

  const peek = (token: string): boolean => {
    skipWs();
    return src.startsWith(token, pos);
  };

  const consume = (token: string): void => {
    pos += token.length;
  };

  const parsePrimary = (): boolean => {
    skipWs();

    // Grouped sub-expression: ( ... )
    if (peek('(')) {
      consume('(');
      const value = parseOr();
      skipWs();
      if (!peek(')')) throw new Error('Expected closing ")"');
      consume(')');
      return value;
    }

    // Otherwise scan a leaf comparison. Stop at an operator or a closing paren,
    // but skip over any [...] list literal and any (...) call argument so
    // commas/operators/parens inside them are never treated as expression
    // boundaries. A leading "(" is handled above as a group, so any "(" seen
    // here belongs to a call such as `.includes(key)`.
    const start = pos;
    while (pos < src.length) {
      const ch = src[pos];
      if (ch === '[') {
        const close = src.indexOf(']', pos);
        if (close === -1) throw new Error('Unclosed "["');
        pos = close + 1;
        continue;
      }
      if (ch === '(') {
        const close = src.indexOf(')', pos);
        if (close === -1) throw new Error('Unclosed "("');
        pos = close + 1;
        continue;
      }
      if (ch === ')') break;
      if (src.startsWith('&&', pos) || src.startsWith('||', pos)) break;
      pos++;
    }

    return evalComparison(src.slice(start, pos), ctx);
  };

  const parseUnary = (): boolean => {
    skipWs();
    // "!" is negation, but "![" begins the leaf `![...].includes(key)` form,
    // so it must NOT be consumed as a unary operator here.
    if (peek('!') && !peek('![')) {
      consume('!');
      return !parseUnary();
    }
    return parsePrimary();
  };

  const parseAnd = (): boolean => {
    let value = parseUnary();
    while (peek('&&')) {
      consume('&&');
      // IMPORTANT: always parse the right operand so the cursor advances, even
      // when `value` is already false. Parsing has the side effect of consuming
      // tokens; a JS short-circuit like `value = value && parseUnary()` would
      // skip that call and leave the parser stuck. We capture the operand first,
      // then combine — which still yields left-to-right boolean semantics.
      const right = parseUnary();
      value = value && right;
    }
    return value;
  };

  function parseOr(): boolean {
    let value = parseAnd();
    while (peek('||')) {
      consume('||');
      // Same reasoning as parseAnd: always parse to advance the cursor. A
      // `value = value || parseAnd()` short-circuit would skip the right operand
      // whenever `value` is already true and leave the group after `||` unparsed.
      const right = parseAnd();
      value = value || right;
    }
    return value;
  }

  const result = parseOr();
  skipWs();
  if (pos < src.length) {
    throw new Error(`Unexpected token near "${src.slice(pos)}"`);
  }
  return result;
};

/**
 * Evaluate a stored boolean condition against a runtime context.
 *
 * @param condition JS-style boolean expression string from the database.
 * @param ctx       Runtime key -> value map. All values compared as strings.
 * @returns whether the associated item should be visible. Fails open (true).
 */
export function evalCondition(
  condition: string | null | undefined,
  ctx: ConditionContext
): boolean {
  if (!condition || !condition.trim()) return true;

  try {
    return parseExpression(condition, ctx);
  } catch (err) {
    // Fail open: a malformed DB string should never hide a menu item.
    console.warn(
      `evalCondition: failed to parse condition "${condition}":`,
      err
    );
    return true;
  }
}

export default evalCondition;
